/**
 * The one place a `TaskState` is born.
 *
 * Every seam that lands a task in the store goes through here — the store's
 * `_createTask`, the send path's optimistic new task, sidebar navigation, the
 * composer seed — so a field added to `TaskState` gets its default in exactly
 * one place. Its own module (not taskStore.ts) because the extracted action
 * bodies need it and importing the store back would close a cycle.
 */

import { DEFAULT_AGENT, type TaskState } from "./types";

/** Build a fresh TaskState with sensible defaults. */
export function buildNewTask(
  taskId: string,
  agentSlug = DEFAULT_AGENT,
  overrides: Partial<TaskState> = {}
): TaskState {
  return {
    taskId,
    messages: [],
    streamingMessage: null,
    phase: "idle",
    title: "New Task",
    createdAt: new Date(),
    lastActivityAt: new Date(),
    agentSlug,
    // Native is the only engine. This used to default to 'claude' under a
    // comment claiming the engine did too — false since the sandbox default was
    // fixed. Still hydrated from the server (tasks:list / MESSAGE_SYNC) so a
    // historical task shows its stored value; the composer reads it read-only.
    runtime: "native",
    // Pinned model id (§4.7) — creation writes the real value from the picker
    // (cluster UI/ROUTE); the default is undefined ⇒ server resolveAgentModel.
    model: undefined,
    // Access level (permissions v2) — null = inherit the user default until the
    // server DTO hydrates a per-task override.
    accessLevel: null,
    // Durable live-preview pointer — null until the server DTO says this task
    // has an app on the sandbox's reserved port.
    livePreviewPointer: null,
    pendingInteraction: null,
    pendingQueue: [],
    pendingByLane: {},
    tokenUsage: { input: 0, output: 0 },
    turnSummary: null,
    turnStartTime: null,
    agentStartedAt: null,
    turnSealedAt: null,
    outputs: [],
    todos: [],
    promptSuggestion: null,
    ...overrides,
  };
}
